import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Activity, Plus, Image as ImageIcon, MapPin, Calendar, X, Church, Building2, Trash2, Eye } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { PhotoLightbox } from "@/components/PhotoLightbox";

const CATEGORIES = [
  { key: "IBADAH", label: "Ibadah", tone: "info" },
  { key: "PELAYANAN", label: "Pelayanan", tone: "success" },
  { key: "SOSIAL", label: "Sosial / Diakonia", tone: "warning" },
  { key: "BINA_USAHA", label: "Bina Usaha", tone: "gold" },
  { key: "RAPAT", label: "Rapat / Sidang", tone: "muted" },
  { key: "LAINNYA", label: "Lainnya", tone: "muted" },
];

async function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

const fmtDate = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("id-ID", { day: "2-digit", month: "long", year: "numeric" }); }
  catch { return d; }
};

export default function Kegiatan() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [churches, setChurches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scopeFilter, setScopeFilter] = useState("ALL");
  const [catFilter, setCatFilter] = useState("ALL");
  const [openNew, setOpenNew] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailPhotos, setDetailPhotos] = useState([]);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: "", description: "", date: new Date().toISOString().slice(0, 10),
    location: "", category: "IBADAH", scope: user?.church_id ? "JEMAAT" : "KLASIS",
    church_id: user?.church_id || "", photos_base64: [], visibility: "PUBLIC",
  });

  const canPost = ["SUPER_ADMIN","ADMIN_KLASIS","SEKRETARIS_KLASIS","KETUA_KLASIS","WAKIL_KETUA","BENDAHARA_KLASIS","BP2K","KOORDINATOR_BIDANG","GEMBALA_JEMAAT","SEKRETARIS_JEMAAT","BENDAHARA_JEMAAT"].includes(user?.role);
  const canPostKlasis = ["SUPER_ADMIN","ADMIN_KLASIS","SEKRETARIS_KLASIS","KETUA_KLASIS","WAKIL_KETUA","BENDAHARA_KLASIS","BP2K","KOORDINATOR_BIDANG"].includes(user?.role);
  const isKlasisAdmin = ["SUPER_ADMIN","ADMIN_KLASIS","SEKRETARIS_KLASIS"].includes(user?.role);

  const load = async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (scopeFilter !== "ALL") q.set("scope", scopeFilter);
      if (catFilter !== "ALL") q.set("category", catFilter);
      const [k, c] = await Promise.all([
        api.get(`/kegiatan?${q.toString()}`),
        churches.length ? Promise.resolve({ data: churches }) : api.get("/churches"),
      ]);
      setItems(k.data);
      if (!churches.length) setChurches(c.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [scopeFilter, catFilter]);

  const openDetail = async (k) => {
    try {
      const { data } = await api.get(`/kegiatan/${k.id}`);
      setDetail(data);
      setDetailPhotos(data.photos_base64 || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const closeDetail = () => { setDetail(null); setDetailPhotos([]); };

  const addPhoto = async (files) => {
    const list = Array.from(files || []).slice(0, 10 - form.photos_base64.length);
    for (const f of list) {
      if (f.size > 1_500_000) { toast.error(`${f.name} > 1.5MB`); continue; }
      const b64 = await fileToBase64(f);
      setForm(prev => ({ ...prev, photos_base64: [...prev.photos_base64, b64] }));
    }
  };

  const removePhoto = (idx) => setForm(prev => ({ ...prev, photos_base64: prev.photos_base64.filter((_, i) => i !== idx) }));

  const submit = async () => {
    if (!form.title.trim()) { toast.error("Judul wajib"); return; }
    if (!form.date) { toast.error("Tanggal wajib"); return; }
    if (form.scope === "JEMAAT" && !form.church_id) { toast.error("Pilih jemaat"); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (form.scope === "KLASIS") payload.church_id = null;
      const { data } = await api.post("/kegiatan", payload);
      toast.success(`Kegiatan "${data.title}" berhasil dibuat.`);
      setOpenNew(false);
      setForm({
        title: "", description: "", date: new Date().toISOString().slice(0, 10),
        location: "", category: "IBADAH", scope: user?.church_id ? "JEMAAT" : "KLASIS",
        church_id: user?.church_id || "", photos_base64: [], visibility: "PUBLIC",
      });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus kegiatan ini?")) return;
    try {
      await api.delete(`/kegiatan/${id}`);
      toast.success("Kegiatan dihapus");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const churchName = (id) => churches.find(c => c.id === id)?.short_name || "—";
  const catLabel = (k) => CATEGORIES.find(c => c.key === k)?.label || k;
  const catTone = (k) => CATEGORIES.find(c => c.key === k)?.tone || "muted";

  return (
    <AppShell>
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-xs tracking-[0.18em] uppercase text-muted-foreground">Pelayanan • Kegiatan</div>
          <h1 className="font-heading font-bold text-3xl mt-1 flex items-center gap-3">
            <Activity className="h-7 w-7 text-secondary" /> Kegiatan & Aktivitas
          </h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Dokumentasi kegiatan tingkat Jemaat maupun BPK Klasis Mimika dengan foto — otomatis tampil di portal publik.
          </p>
        </div>
        {canPost && (
          <Dialog open={openNew} onOpenChange={setOpenNew}>
            <DialogTrigger asChild>
              <Button className="rounded-full" data-testid="btn-new-kegiatan"><Plus className="h-4 w-4 mr-2" /> Upload Kegiatan</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Upload Kegiatan</DialogTitle>
                <DialogDescription>Isi detail kegiatan, unggah foto (maks 10), dan pilih visibilitas.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div><Label>Judul Kegiatan</Label><Input value={form.title} onChange={(e)=>setForm({...form, title:e.target.value})} placeholder="Ibadah Persekutuan Perdana" data-testid="input-keg-title" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Tanggal</Label>
                    <Input type="date" value={form.date} onChange={(e)=>setForm({...form, date:e.target.value})} data-testid="input-keg-date" />
                  </div>
                  <div>
                    <Label>Kategori</Label>
                    <Select value={form.category} onValueChange={(v)=>setForm({...form, category:v})}>
                      <SelectTrigger data-testid="select-keg-category"><SelectValue /></SelectTrigger>
                      <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Lokasi</Label>
                  <Input value={form.location} onChange={(e)=>setForm({...form, location:e.target.value})} placeholder="GPDP Jemaat El Olam Timika" data-testid="input-keg-location" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Lingkup</Label>
                    <Select value={form.scope} onValueChange={(v)=>setForm({...form, scope:v})}>
                      <SelectTrigger data-testid="select-keg-scope"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="JEMAAT">Jemaat</SelectItem>
                        {canPostKlasis && <SelectItem value="KLASIS">BPK Klasis Mimika</SelectItem>}
                      </SelectContent>
                    </Select>
                  </div>
                  {form.scope === "JEMAAT" && (
                    <div>
                      <Label>Jemaat</Label>
                      <Select value={form.church_id} onValueChange={(v)=>setForm({...form, church_id:v})} disabled={!isKlasisAdmin && !!user?.church_id}>
                        <SelectTrigger data-testid="select-keg-church"><SelectValue placeholder="Pilih jemaat" /></SelectTrigger>
                        <SelectContent>
                          {churches.filter(c => c.type === "JEMAAT").map(c => <SelectItem key={c.id} value={c.id}>{c.short_name || c.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
                <div>
                  <Label>Deskripsi</Label>
                  <Textarea rows={4} value={form.description} onChange={(e)=>setForm({...form, description:e.target.value})} placeholder="Ceritakan kegiatan, tema, pembicara, dll." data-testid="input-keg-desc" />
                </div>
                <div>
                  <Label>Foto Kegiatan ({form.photos_base64.length}/10)</Label>
                  <Input type="file" accept="image/*" multiple onChange={(e)=>addPhoto(e.target.files)} disabled={form.photos_base64.length >= 10} data-testid="input-keg-photos" />
                  {form.photos_base64.length > 0 && (
                    <div className="grid grid-cols-5 gap-2 mt-2">
                      {form.photos_base64.map((p, i) => (
                        <div key={i} className="relative">
                          <img src={p} alt={`photo ${i+1}`} className="w-full h-16 object-cover rounded border border-border" />
                          <button type="button" onClick={()=>removePhoto(i)} className="absolute -top-1 -right-1 bg-rose-600 text-white rounded-full p-0.5" data-testid={`btn-remove-photo-${i}`}>
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <Label>Visibility</Label>
                  <Select value={form.visibility} onValueChange={(v)=>setForm({...form, visibility:v})}>
                    <SelectTrigger data-testid="select-keg-visibility"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PUBLIC">Publik (tampil di portal publik)</SelectItem>
                      <SelectItem value="INTERNAL">Internal (hanya pengurus)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={submit} disabled={saving} data-testid="btn-submit-kegiatan">{saving ? "Menyimpan..." : "Simpan Kegiatan"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <Select value={scopeFilter} onValueChange={setScopeFilter}>
          <SelectTrigger className="w-48" data-testid="select-filter-scope"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Semua Lingkup</SelectItem>
            <SelectItem value="JEMAAT">Jemaat</SelectItem>
            <SelectItem value="KLASIS">BPK Klasis</SelectItem>
          </SelectContent>
        </Select>
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger className="w-56" data-testid="select-filter-category"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Semua Kategori</SelectItem>
            {CATEGORIES.map(c => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="ml-auto text-xs text-muted-foreground">{items.length} kegiatan</div>
      </div>

      {loading ? <div className="text-sm text-muted-foreground">Memuat…</div> : items.length === 0 ? (
        <div className="border border-dashed border-border rounded-lg p-12 text-center">
          <Activity className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
          <div className="text-sm text-muted-foreground">Belum ada kegiatan.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((k) => (
            <Card key={k.id} className="overflow-hidden hover:border-primary/40 transition-colors" data-testid={`card-kegiatan-${k.id}`}>
              <div className="h-32 bg-gradient-to-br from-primary/10 to-secondary/10 flex items-center justify-center border-b border-border">
                {k.photo_count > 0 ? (
                  <div className="flex items-center gap-1 text-primary font-semibold">
                    <ImageIcon className="h-8 w-8" />
                    <span className="text-2xl">{k.photo_count}</span>
                    <span className="text-xs text-muted-foreground ml-1">foto</span>
                  </div>
                ) : (
                  <Activity className="h-10 w-10 text-muted-foreground" />
                )}
              </div>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1.5">
                  <StatusBadge status={catTone(k.category)} label={catLabel(k.category)} />
                  {k.scope === "KLASIS" ? (
                    <span className="text-[0.65rem] font-semibold bg-primary/10 text-primary px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                      <Building2 className="h-3 w-3" /> BPK Klasis
                    </span>
                  ) : (
                    <span className="text-[0.65rem] font-semibold bg-muted text-muted-foreground px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                      <Church className="h-3 w-3" /> {churchName(k.church_id)}
                    </span>
                  )}
                  {k.visibility === "INTERNAL" && (
                    <span className="text-[0.6rem] font-semibold bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">INTERNAL</span>
                  )}
                </div>
                <div className="font-heading font-bold text-base leading-tight line-clamp-2">{k.title}</div>
                <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                  <Calendar className="h-3 w-3" /> {fmtDate(k.date)}
                </div>
                {k.location && (
                  <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1 truncate">
                    <MapPin className="h-3 w-3" /> {k.location}
                  </div>
                )}
                {k.description && (
                  <p className="text-xs text-foreground/80 mt-2 line-clamp-2">{k.description}</p>
                )}
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
                  <button onClick={()=>openDetail(k)} className="text-xs text-primary hover:underline flex items-center gap-1" data-testid={`btn-detail-kegiatan-${k.id}`}>
                    <Eye className="h-3 w-3" /> Detail
                  </button>
                  {(isKlasisAdmin || k.created_by_id === user?.id) && (
                    <button onClick={()=>del(k.id)} className="text-xs text-rose-600 hover:underline flex items-center gap-1" data-testid={`btn-del-kegiatan-${k.id}`}>
                      <Trash2 className="h-3 w-3" /> Hapus
                    </button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && closeDetail()}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading">{detail.title}</DialogTitle>
                <DialogDescription>{detail.category} · {detail.scope === "KLASIS" ? "BPK Klasis Mimika" : churchName(detail.church_id)}</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div className="flex items-center gap-2 flex-wrap text-xs">
                  <StatusBadge status={catTone(detail.category)} label={catLabel(detail.category)} />
                  <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> {fmtDate(detail.date)}</span>
                  {detail.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {detail.location}</span>}
                  {detail.scope === "KLASIS" ? (
                    <span className="inline-flex items-center gap-1"><Building2 className="h-3 w-3" /> BPK Klasis Mimika</span>
                  ) : (
                    <span className="inline-flex items-center gap-1"><Church className="h-3 w-3" /> {churchName(detail.church_id)}</span>
                  )}
                </div>
                {detail.description && <div className="text-sm whitespace-pre-wrap text-foreground/90">{detail.description}</div>}
                {detailPhotos.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {detailPhotos.map((p, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => { setLightboxIndex(i); setLightboxOpen(true); }}
                        className="relative group rounded overflow-hidden border border-border hover:border-primary transition"
                        data-testid={`btn-open-lightbox-${i}`}
                      >
                        <img src={p} alt={`foto ${i+1}`} className="w-full h-32 object-cover" />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition flex items-center justify-center opacity-0 group-hover:opacity-100">
                          <span className="text-white text-xs font-semibold bg-black/60 px-2 py-1 rounded">Klik untuk perbesar</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                <div className="text-[0.7rem] text-muted-foreground pt-2 border-t border-border">
                  Diposting oleh {detail.created_by} pada {new Date(detail.created_at).toLocaleString("id-ID")}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Photo Lightbox */}
      <PhotoLightbox
        photos={detailPhotos}
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        initialIndex={lightboxIndex}
        title={detail?.title}
      />
    </AppShell>
  );
}
